'use client'

import { useContext } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import Pill from '@/components/ui/Pill'
import Link from '@/components/ui/Link'
import { PREFETCH } from '@/lib/prefetch'
import RecommendationBadge from '@/components/ui/RecommendationBadge'
import Menu from '@/components/ui/Menu'
import AppIcon from '@/components/content/app/AppIcon'
import PageContext from '@/components/content/common/PageContext'
import { getAppRecommendation } from '@/components/content/app/helpers'
import { getBrandColors, cn } from '@/lib/utils'
import { scrollToSection } from '@/lib/utils'
import type { App, Category } from '@/payload-types'

const container = cva('grid grid-cols-1 gap-y-5', {
  variants: {
    cols: {
      1: '',
      2: 'md:grid-cols-2 md:gap-x-12',
    },
  },
})

type AppListProps = VariantProps<typeof container> & {
  title?: React.ReactNode
  apps: App[]
  cols?: number
  menu?: any
  label?: string
  categoryId?: Category['id']
}

export default function AppList({ title, apps, cols = 1, menu, label, categoryId }: AppListProps) {
  const validApps = apps.filter((app) => app.slug)
  if (validApps.length === 0) return null

  return (
    <div className="flex w-full flex-col gap-4">
      {title && title}

      <div className={cn(container({ cols }))}>
        {validApps.map((app) => {
          return (
            <AppItem
              key={app.id}
              app={app}
              label={label}
              menu={menu ? menu(app) : undefined}
              categoryId={categoryId}
            />
          )
        })}
      </div>
    </div>
  )
}

export function AppItem({
  app,
  label,
  menu,
  categoryId,
}: {
  app: App
  label?: string
  menu?: any
  categoryId?: Category['id']
}) {
  const context = useContext(PageContext)

  if (!app.slug) return null

  const link = `/apps/${app.slug}`
  const buttonClass = 'flex w-full items-center gap-3.5 md:w-[calc(100%-90px)]'

  const { recommendation, bestFor } = getAppRecommendation({ app, categoryId: categoryId || 0 })
  const { backgroundColor } = getBrandColors(app)

  const appContent = (
    <>
      <AppIcon
        icon={app.icon}
        glyph={app.glyph}
        name={app.name}
        background={backgroundColor}
        size={56}
      />

      <div className="mt-1 flex flex-col gap-1">
        <div className="flex h-5 w-full items-center gap-2.5">
          <span className="line-clamp-1 font-semibold text-gray-950">{app.name}</span>
          <RecommendationBadge recommendation={recommendation} />
        </div>
        <span aria-hidden="true" className="line-clamp-1 w-full text-left text-13 text-gray-600">
          {bestFor || app.description}
        </span>
      </div>
    </>
  )

  const switchFocus = async () => {
    if (context?.setAppFocus && app.name) {
      scrollToSection({ id: 'content', offset: 45, animate: true, hideHash: true })
      context.setAppFocus(app.name)
    }
  }

  return (
    <div key={app.id} className="flex items-center justify-between gap-5 md:gap-10">
      {label ? (
        <button
          onClick={switchFocus}
          type="button"
          aria-label={`Focus on ${app.name}`}
          className={buttonClass}
        >
          {appContent}
        </button>
      ) : (
        <Link
          href={link}
          prefetch={PREFETCH}
          className={buttonClass}
          aria-label={`Read ${app.name} review`}
        >
          {appContent}
        </Link>
      )}

      <div className="flex gap-3">
        {label ? (
          <button
            onClick={switchFocus}
            type="button"
            aria-label={`Focus on ${app.name}`}
            className="hidden sm:flex"
          >
            <Pill as="span" style="outlinePrimary" className="ml-auto">
              Go to {label}
            </Pill>
          </button>
        ) : (
          <Link
            href={link}
            prefetch={PREFETCH}
            className="hidden sm:flex"
            aria-label={`Read ${app.name} review`}
          >
            <Pill as="span" style="outlinePrimary" className="ml-auto">
              Read Review
            </Pill>
          </Link>
        )}
        {menu && <Menu items={menu} align="right" />}
      </div>
    </div>
  )
}
